/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **A parent's row that this artifact stopped overriding** — the model, the two acts and the
 * rendering, shared by every keyed-row editor that has a parent (`#1126`, `#1422`).
 *
 * Todd, 2026-09-18: *"a parent overrides a sent-automatically field, and the author wants to stop
 * overriding. That's not a suppress — it's overriding the parent's override."* The parameter editor
 * built it for `#1126`. When the driver's own headers were declared (`4b-ii`), a parent's
 * `X-Forwarded-For` row displaced an automatic value in exactly the same way, and the headers editor
 * needed the same act (`#1422`). It is lifted here rather than copied: which rows displace an
 * automatic value is each surface's declaration, but *what stopping overriding is* is not.
 *
 * Stored as a **removal**, a `null` in this artifact's layer (`artifact-layer.ts`), carried on the
 * document as `removedFromParent`. The resolver applies it, so every reader sees the key simply gone.
 */

import React from 'react';
import { Button } from '@chakra-ui/react';
import { AutomaticRow } from './AutomaticPanel';

/**
 * **What this artifact removes from its parent, by row name.**
 *
 * `null` is a surface with no parent (the wizard). `stored` is what the saved artifact removes, whose
 * way back is revert; a name in `names` and not in `stored` was removed in this draft, and its way
 * back is Undo. `onChange` is `null` where nothing may change it (a read-only preview), and carries
 * the rows with the names because one gesture changes both.
 */
export interface ParentRemovals<Row> {
  names: readonly string[];
  stored: readonly string[];
  onChange: ((next: { names: string[]; rows: Row[] }) => void) | null;
}

/**
 * *Stop overriding* and its Undo, over one editor's rows.
 *
 * Undo puts back exactly the row that was there rather than whatever the parent sends, so the rows
 * removed in this draft are kept for the session. A stored removal has no row to keep, and its way
 * back is revert.
 */
export function useStopOverriding<Row>({
  parentRemovals,
  rows,
  nameOf,
  isSameRow,
  onRowLeaving,
  commit,
}: {
  parentRemovals: ParentRemovals<Row> | null;
  rows: Row[];
  nameOf: (row: Row) => string;
  isSameRow: (a: Row, b: Row) => boolean;
  /** Close the row's editor if it is open — the row is about to leave the list. */
  onRowLeaving: (row: Row) => void;
  /** A discrete change, so the preview resolves (R4e). */
  commit: () => void;
}): { stopOverriding: ((row: Row) => void) | null; undo: (name: string) => void } {
  const [removedThisDraft, setRemovedThisDraft] = React.useState<Readonly<Record<string, Row>>>({});
  const onChange = parentRemovals?.onChange ?? null;

  const stopOverriding = (row: Row) => {
    if (!parentRemovals || !onChange) return;
    const name = nameOf(row);
    setRemovedThisDraft((kept) => ({ ...kept, [name]: row }));
    onRowLeaving(row);
    onChange({
      names: [...parentRemovals.names.filter((n) => n !== name), name],
      rows: rows.filter((r) => !isSameRow(r, row)),
    });
    commit();
  };

  const undo = (name: string) => {
    if (!parentRemovals || !onChange) return;
    const row = removedThisDraft[name];
    setRemovedThisDraft(({ [name]: _undone, ...kept }) => kept);
    onChange({
      names: parentRemovals.names.filter((n) => n !== name),
      rows: row && !rows.some((r) => nameOf(r) === name) ? [...rows, row] : rows,
    });
    commit();
  };

  return { stopOverriding: onChange ? stopOverriding : null, undo };
}

/**
 * **The removed rows, in the surface's *Sent automatically* panel** (Todd 2026-09-18: *"the line
 * reappears in the sent automatically box with 'overrides parent'"*). There rather than as a greyed
 * row among the authored ones, because what goes out for them is what that panel lists, and one
 * place per field is the rule a second rendering would break.
 */
export const ParentRemovalRows: React.FC<{
  // `never`: this only reads the names and whether it may change them, so any row type fits.
  removals: ParentRemovals<never> | null;
  parentName: string | undefined;
  readOnly: boolean;
  /** What goes out in the parent row's place, for the sentence: *the value sent automatically*. */
  insteadOf: (name: string) => string;
  /** Give the parent's row back — a server write. `null` where revert is not offered here. */
  revert: ((paths: string[]) => void) | null;
  /** True when revert takes effect at once, inside an open draft (`useDraftRevert`). */
  revertsNow: boolean;
  isReverting: boolean | undefined;
  pathsOf: (name: string) => string[];
  undo: (name: string) => void;
}> = ({ removals, parentName, readOnly, insteadOf, revert, revertsNow, isReverting, pathsOf, undo }) => {
  const from = parentName ?? 'the parent';
  return (
    <>
      {(removals?.names ?? []).map((name) => {
        const stored = removals?.stored.includes(name) === true;
        return (
          <AutomaticRow
            key={`removed:${name}`}
            name={name}
            // Both states say what **is** sent. The unsaved one said only what would not be, which
            // in a panel titled *Sent automatically* read as *nothing goes out* (Todd, `#1422`).
            detail={
              stored
                ? `${from}'s row is not sent from this source, so ${insteadOf(name)} goes out instead.`
                : `Once you save, ${insteadOf(name)} goes out instead of ${from}'s row.`
            }
            mark={{ label: `overrides ${from}` }}
            resolution={null}
            action={
              readOnly ? null : stored ? (
                revert ? (
                  <Button
                    size="xs"
                    variant="ghost"
                    color="accent.solid"
                    flexShrink={0}
                    disabled={isReverting}
                    title={`Send ${from}'s row from this source again.${revertsNow ? ' Takes effect now, not on Save.' : ''}`}
                    aria-label={`Revert ${name} to ${from}'s row`}
                    onClick={() => revert(pathsOf(name))}
                  >
                    revert
                  </Button>
                ) : null
              ) : removals?.onChange ? (
                <Button
                  size="xs"
                  variant="ghost"
                  color="accent.solid"
                  flexShrink={0}
                  aria-label={`Undo removing ${name}`}
                  onClick={() => undo(name)}
                >
                  Undo
                </Button>
              ) : null
            }
          />
        );
      })}
    </>
  );
};
